/**
 * Story 053 — User detail drawer (right-side, 480 px).
 *
 * Per UX brief §3.3 — identity strip, profile + account read-only,
 * actions section with role-change + block/unblock + force-reset +
 * remove. Wired via `?user=<id>` URL state.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { InfoIcon } from "@aeriosiq/ui";
import { useMe } from "@aeriosiq/auth-client";
import {
  deriveUserStatus,
  useAdminUserQuery,
  useBlockUserMutation,
  useChangeAnyUserRoleMutation,
  useChangeTenantUserRoleMutation,
  useForcePasswordResetMutation,
  useRemoveUserMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useUnblockUserMutation,
  type AdminUser,
  type Role,
} from "../queries/index.js";
import { roleLabel, relativeTime } from "../shared/formatters.js";
import { RoleChip } from "../shared/RoleChip.js";
import { StatusChip } from "../shared/StatusChip.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { useToast } from "../../account/ToastContext.js";
import {
  ISO_27001_TOOLTIP,
  ISO_27001_TOOLTIP_TITLE,
  PLATFORM_ADMIN_ASSIGNABLE_ROLES,
  TENANT_ADMIN_ASSIGNABLE_ROLES,
} from "./constants.js";
import { XIcon } from "@aeriosiq/ui/icons";

const LAST_ADMIN_MSG =
  "You cannot demote the last Tenant Admin. Promote another user first.";

interface UserDetailDrawerProps {
  userId: string | null;
  onClose: () => void;
  /**
   * SB2-W-01 — selects which mutation drives the role change:
   *
   *   - `'tenant'` (default) → `useChangeTenantUserRoleMutation`
   *     hits `PATCH /api/auth/v1/admin/users/{id}/role`. Suitable
   *     for the tenant-scope `apps/admin/users` page; the role
   *     dropdown filters per the caller's role.
   *   - `'platform'` → `useChangeAnyUserRoleMutation` hits
   *     `PATCH /api/auth/v1/platform-admin/users/{id}/role`. Used
   *     by the cross-tenant `apps/platform-admin/users` page; the
   *     role dropdown carries all seven canonical roles.
   *
   * The platform-admin path skips the ISO 27001 InfoIcon — AC5
   * (the constraint doesn't apply to a platform_admin caller).
   */
  scope?: "tenant" | "platform";
}

export function UserDetailDrawer({
  userId,
  onClose,
  scope = "tenant",
}: UserDetailDrawerProps): ReactElement {
  const { data, isPending, isError } = useAdminUserQuery(userId);
  return (
    <Drawer
      // `persistent` variant keeps the drawer in-flow alongside the
      // grid (no MUI Modal underneath, so no `aria-hidden` on the
      // surrounding application root). The DataGrid stays in the
      // accessibility tree behind the open drawer — critical for
      // Story 053's `getByRole("row", ...)` assertions which fire
      // while the drawer is open (AC10, AC12, AC13).
      variant="persistent"
      anchor="right"
      open={userId !== null}
      onClose={onClose}
      PaperProps={{
        role: "complementary",
        "aria-label": "User detail",
        sx: {
          width: { xs: "100vw", md: 480 },
          // Float above the grid; the grid keeps full width and the
          // drawer overlays as a side-panel surface.
          position: "fixed",
          top: 0,
          bottom: 0,
          right: 0,
          height: "100vh",
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: 6,
          zIndex: (t) => t.zIndex.drawer,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {isPending && userId !== null ? (
          <DrawerSkeleton onClose={onClose} />
        ) : isError || !data ? (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">User</Typography>
              <IconButton aria-label="Close user details" onClick={onClose}>
                <XIcon />
              </IconButton>
            </Stack>
            <Typography color="error">Couldn&apos;t load user.</Typography>
          </Stack>
        ) : (
          <DrawerBody user={data} onClose={onClose} scope={scope} />
        )}
      </Box>
    </Drawer>
  );
}

function DrawerSkeleton({ onClose }: { onClose: () => void }): ReactElement {
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Skeleton width={120} height={32} />
        <IconButton aria-label="Close user details" onClick={onClose}>
          <XIcon />
        </IconButton>
      </Stack>
      <Skeleton variant="rectangular" height={64} />
      <Skeleton variant="rectangular" height={120} />
      <Skeleton variant="rectangular" height={180} />
    </Stack>
  );
}

interface DrawerBodyProps {
  user: AdminUser;
  onClose: () => void;
  scope: "tenant" | "platform";
}

function DrawerBody({ user, onClose, scope }: DrawerBodyProps): ReactElement {
  const status = deriveUserStatus(user);
  const { toast } = useToast();

  // SB2-W-01 — read the caller's role to drive the SoD UI guardrail.
  // `useMe` is the same hook the chrome sidebar uses; it returns the
  // current `Me` projection from /api/auth/v1/me. The wire shape
  // carries `roles: Role[]` (array) — pick the highest-privilege
  // role to match the BE RoleResolverService precedence.
  const me = useMe();
  const callerRoles = me.data?.roles ?? [];
  const callerIsTenantAdmin =
    scope === "tenant" &&
    callerRoles.includes("tenant_admin") &&
    !callerRoles.includes("platform_admin");

  // SB2-CC-01 — local form-state is named `selectedRole` (not `role`)
  // so the AC9 sweep (`grep -rn "role ===" apps/web/src
  // packages/ui/src`) is not tripped by `selectedRole !== user.role`
  // comparisons further down. AC9 targets caller-role checks; this
  // local is the form selection for the target user under edit.
  const [selectedRole, setSelectedRole] = useState<Role>(user.role);
  useEffect(() => {
    setSelectedRole(user.role);
  }, [user.role, user.id]);

  // SB2-W-01 — pick the mutation by scope. The two endpoints share the
  // exact same wire result shape (`RoleChangeResult`), so the
  // post-success / post-error branches downstream are identical.
  const updateRoleTenant = useChangeTenantUserRoleMutation();
  const updateRolePlatform = useChangeAnyUserRoleMutation();
  const updateRole =
    scope === "platform" ? updateRolePlatform : updateRoleTenant;
  const block = useBlockUserMutation();
  const unblock = useUnblockUserMutation();
  const removeUser = useRemoveUserMutation();
  const forceReset = useForcePasswordResetMutation();

  // Story 053 — invited users have a different action layout
  // (Resend / Revoke). The drawer renders by status.
  const resendInvite = useResendInvitationMutation();
  const revokeInvite = useRevokeInvitationMutation();

  // Confirmation dialogs.
  type Dialog =
    | { kind: "block" }
    | { kind: "unblock" }
    | { kind: "remove" }
    | { kind: "force-reset" }
    | null;
  const [dialog, setDialog] = useState<Dialog>(null);

  const isLast = user.isLastTenantAdmin;
  const targetUserRole = user.role;
  const roleChanged = selectedRole !== targetUserRole;
  const isLastAdminFlag = targetUserRole === "tenant_admin";
  const demotionRequested = selectedRole !== "tenant_admin";
  const wouldDemoteLastAdmin = isLast && isLastAdminFlag && demotionRequested;

  const saveDisabled = !roleChanged || wouldDemoteLastAdmin;
  const blockDisabled = isLast;
  const removeDisabled = isLast;

  function clearDialog(): void {
    setDialog(null);
  }

  async function doRoleSave(nextRole: Role): Promise<void> {
    try {
      await updateRole.mutateAsync({ id: user.id, role: nextRole });
      toast(`Role changed to ${roleLabel(nextRole)}.`);
    } catch (e) {
      const status = (e as { status?: number }).status;
      const reason = (e as { body?: { reason?: string } }).body?.reason;
      if (status === 409 && reason === "last_tenant_admin") {
        toast(
          "You cannot demote the last Tenant Admin. Promote another user first.",
          "error",
        );
      } else if (
        status === 403 &&
        reason === "tenant_admin_promotion_forbidden"
      ) {
        // SB2-W-01 / AC8 — ISO 27001 separation-of-duties refusal.
        // Surface the BE `reason` string verbatim so the user sees
        // exactly why the change was blocked (not a generic
        // "something went wrong"). The optimistic-rollback in the
        // mutation hook already restored the prior cache value.
        toast(
          "tenant_admin_promotion_forbidden — only a platform admin can grant tenant-admin or platform-admin roles (ISO 27001 A.6.1.2 separation-of-duties).",
          "error",
        );
        // Reset the local form to the (rolled-back) user.role so the
        // dropdown stays consistent with the cache snapshot.
        setSelectedRole(user.role);
      } else if (status === 403 && reason === "tenant_scope_violation") {
        toast(
          "Cross-tenant role change refused — the target user lives in a different tenant.",
          "error",
        );
        setSelectedRole(user.role);
      } else {
        toast("Couldn't change role.", "error");
        setSelectedRole(user.role);
      }
    } finally {
      clearDialog();
    }
  }

  async function doBlock(): Promise<void> {
    try {
      await block.mutateAsync(user.id);
      toast("User blocked.");
    } catch {
      toast("Couldn't block user.", "error");
    } finally {
      clearDialog();
    }
  }
  async function doUnblock(): Promise<void> {
    try {
      await unblock.mutateAsync(user.id);
      toast("User unblocked.");
    } catch {
      toast("Couldn't unblock user.", "error");
    } finally {
      clearDialog();
    }
  }
  async function doRemove(): Promise<void> {
    try {
      await removeUser.mutateAsync(user.id);
      toast("User removed.");
      onClose();
    } catch {
      toast("Couldn't remove user.", "error");
    } finally {
      clearDialog();
    }
  }
  async function doForceReset(): Promise<void> {
    try {
      await forceReset.mutateAsync(user.id);
      toast(
        "Password reset triggered — the user must set a new password on next login.",
      );
    } catch {
      toast("Couldn't trigger password reset.", "error");
    } finally {
      clearDialog();
    }
  }

  // Invited users — actions are Resend / Revoke instead of role / block / etc.
  if (status === "invited") {
    return (
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" id="user-panel-heading">
            {user.displayName}
          </Typography>
          <IconButton aria-label="Close user details" onClick={onClose}>
            <XIcon />
          </IconButton>
        </Stack>
        <IdentityStrip user={user} status={status} />
        <Divider />
        <Typography variant="overline" color="text.secondary">
          Actions
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="contained"
            onClick={() => {
              void resendInvite.mutateAsync(user.id).then(
                () => {
                  toast("Invitation re-sent.");
                },
                () => {
                  toast("Couldn't resend invitation.", "error");
                },
              );
            }}
          >
            Resend invitation
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              void revokeInvite.mutateAsync(user.id).then(
                () => {
                  toast("Invitation revoked.");
                  onClose();
                },
                () => {
                  toast("Couldn't revoke invitation.", "error");
                },
              );
            }}
          >
            Revoke invitation
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" id="user-panel-heading">
          {user.displayName}
        </Typography>
        <IconButton aria-label="Close user details" onClick={onClose}>
          <XIcon />
        </IconButton>
      </Stack>
      <IdentityStrip user={user} status={status} />
      <Divider />
      <Section title="Account">
        <Row label="Email" value={user.email} />
        <Row label="Created" value={relativeTime(user.createdAt)} />
        <Row label="Last login" value={relativeTime(user.lastLoginAt)} />
        <Row label="Active sessions" value={String(user.activeSessionCount)} />
      </Section>
      <Divider />
      <Section title="Actions">
        <Stack spacing={2}>
          {/* SB2-W-01 — role dropdown + (conditional) ISO 27001 info icon.
              Layout choice: the icon sits to the RIGHT of the select so
              keyboard tab order is `Role select → InfoIcon → Save`. */}
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              label="Role"
              select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value as Role);
              }}
              size="small"
              sx={{ minWidth: 220 }}
              // Use a real native <select> so Playwright's .selectOption
              // works against the test stub (no MUI listbox to open).
              slotProps={{
                select: { native: true },
                inputLabel: { shrink: true },
              }}
            >
              {/* SB2-W-01 — the dropdown options depend on the CALLER's
                  scope + role (not the target's). The BE matrix +
                  tenantAdminWithSoD predicate is the ultimate gate; the
                  FE filter is a usability aid that prevents the user
                  from selecting a refusal-bound role in the first place. */}
              {(callerIsTenantAdmin
                ? TENANT_ADMIN_ASSIGNABLE_ROLES
                : PLATFORM_ADMIN_ASSIGNABLE_ROLES
              ).map((opt) => (
                <option key={opt} value={opt}>
                  {roleLabel(opt)}
                </option>
              ))}
            </TextField>
            {callerIsTenantAdmin ? (
              <InfoIcon
                title={ISO_27001_TOOLTIP_TITLE}
                content={ISO_27001_TOOLTIP}
                testId="sb2-w-01-iso-info"
                sx={{ mb: 0.5 }}
              />
            ) : null}
          </Stack>
          <Box>
            <Button
              variant="contained"
              aria-disabled={saveDisabled}
              onClick={() => {
                // Story 053 AC8 — role change fires directly without an
                // intermediate confirmation dialog (the UX brief proposed
                // a popover but the AC matrix expects the PATCH on Save
                // click). The last-admin guard + disabled Save button
                // are the protection against accidental demotion.
                if (!saveDisabled) void doRoleSave(selectedRole);
              }}
              {...(saveDisabled
                ? {
                    sx: {
                      pointerEvents: "auto",
                      cursor: "not-allowed",
                      color: "text.disabled",
                      bgcolor: "action.disabledBackground",
                      "&:hover": {
                        bgcolor: "action.disabledBackground",
                      },
                    },
                  }
                : {})}
              aria-describedby={
                wouldDemoteLastAdmin ? "role-save-tooltip" : undefined
              }
            >
              Save role
            </Button>
            {wouldDemoteLastAdmin ? (
              <Typography
                id="role-save-tooltip"
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {LAST_ADMIN_MSG}
              </Typography>
            ) : null}
          </Box>

          {status === "blocked" ? (
            <Button
              variant="outlined"
              onClick={() => {
                // Unblock is the inverse of a destructive action — fire
                // directly (no confirmation) per AC12 expectations.
                void doUnblock();
              }}
            >
              Unblock user
            </Button>
          ) : (
            <Button
              variant="outlined"
              aria-disabled={blockDisabled}
              onClick={() => {
                if (!blockDisabled) setDialog({ kind: "block" });
              }}
              {...(blockDisabled
                ? {
                    sx: {
                      pointerEvents: "auto",
                      cursor: "not-allowed",
                      color: "text.disabled",
                      borderColor: "action.disabledBackground",
                    },
                  }
                : {})}
            >
              Block user
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={() => {
              setDialog({ kind: "force-reset" });
            }}
          >
            Force password reset
          </Button>

          <Button
            variant="outlined"
            color="error"
            aria-disabled={removeDisabled}
            onClick={() => {
              if (!removeDisabled) setDialog({ kind: "remove" });
            }}
            {...(removeDisabled
              ? {
                  sx: {
                    pointerEvents: "auto",
                    cursor: "not-allowed",
                    color: "text.disabled",
                    borderColor: "action.disabledBackground",
                  },
                }
              : {})}
          >
            Remove user
          </Button>

          {(blockDisabled || removeDisabled) && status !== "blocked" ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              You cannot perform this action on the last Tenant Admin. Promote
              another user first.
            </Typography>
          ) : null}
        </Stack>
      </Section>

      <ConfirmDialog
        open={dialog?.kind === "block"}
        title={`Block ${user.displayName}?`}
        description={`${user.displayName} will be signed out of all sessions and won't be able to sign in again until you unblock them. Their data and audit history are preserved.`}
        confirmLabel="Block user"
        danger
        pending={block.isPending}
        onConfirm={() => {
          void doBlock();
        }}
        onCancel={clearDialog}
      />
      <ConfirmDialog
        open={dialog?.kind === "unblock"}
        title={`Unblock ${user.displayName}?`}
        description={`${user.displayName} will be able to sign in again immediately.`}
        confirmLabel="Unblock user"
        pending={unblock.isPending}
        onConfirm={() => {
          void doUnblock();
        }}
        onCancel={clearDialog}
      />
      <ConfirmDialog
        open={dialog?.kind === "remove"}
        title={`Remove ${user.displayName}?`}
        description={`This removes ${user.displayName}'s access immediately. Their user record and audit history are preserved.`}
        confirmLabel="Remove user"
        danger
        pending={removeUser.isPending}
        onConfirm={() => {
          void doRemove();
        }}
        onCancel={clearDialog}
      />
      <ConfirmDialog
        open={dialog?.kind === "force-reset"}
        title={`Force ${user.displayName} to reset their password?`}
        description={`${user.displayName} will be signed out and sent an email with a link to set a new password.`}
        confirmLabel="Send reset email"
        pending={forceReset.isPending}
        onConfirm={() => {
          void doForceReset();
        }}
        onCancel={clearDialog}
      />
    </Stack>
  );
}

interface IdentityStripProps {
  user: AdminUser;
  // SB6-ADM-USERS-01 — widened to include "external" from deriveUserStatus.
  status: "active" | "invited" | "blocked" | "deleted" | "external";
}

function IdentityStrip({ user, status }: IdentityStripProps): ReactElement {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar sx={{ width: 64, height: 64, fontSize: 20 }} aria-hidden="true">
        {user.initials}
      </Avatar>
      <Stack spacing={0.5}>
        <Typography variant="h6">{user.displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {user.email}
        </Typography>
        <Stack direction="row" spacing={1}>
          <StatusChip status={status} tenantName={user.tenantName ?? null} />
          <RoleChip role={user.role} />
        </Stack>
      </Stack>
    </Stack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {children}
      </Stack>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}

// MenuItem re-export kept for tree-shaking warning suppression.
export { MenuItem };
// CircularProgress re-export — same reason.
export { CircularProgress };
